import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Tenant } from "./api";
import { useSession } from "./session";
import { LoginPage } from "./pages/Login";
import { ExplorerPage } from "./pages/Explorer";
import { TailPage } from "./pages/Tail";
import { IssuesPage } from "./pages/Issues";
import { IssueDetailPage } from "./pages/IssueDetail";
import { SubscriptionsPage } from "./pages/Subscriptions";
import { KeysPage } from "./pages/Keys";
import { TenantsPage } from "./pages/Tenants";

function BrandMark() {
  // Compact signal/pulse glyph in the Nasterra palette (navy field, amber + teal pulse).
  return (
    <svg className="brand-mark" viewBox="0 0 48 40" role="img" aria-hidden="true">
      <rect x="0" y="0" width="48" height="40" rx="11" fill="#0E1B3A" />
      <path
        d="M7 24 h7 l4 -11 l6 20 l4 -13 l3 6 h9"
        fill="none"
        stroke="#F5A623"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="39" cy="26" r="2.6" fill="#2DA6A0" />
    </svg>
  );
}

function TenantPicker() {
  const { tenant, chooseTenant } = useSession();
  const qc = useQueryClient();
  const tenants = useQuery({
    queryKey: ["tenants", "platform"],
    queryFn: () => api<{ tenants: Tenant[] }>("/v1/tenants", { noTenant: true }).then((r) => r.tenants),
  });
  return (
    <label className="tenant-picker">
      Tenant
      <select
        value={tenant ?? ""}
        onChange={(e) => {
          chooseTenant(e.target.value || null);
          qc.invalidateQueries();
        }}
      >
        <option value="">— choose —</option>
        {tenants.data?.map((t) => (
          <option key={t.id} value={t.slug}>
            {t.slug}
          </option>
        ))}
      </select>
    </label>
  );
}

export function App() {
  const { loading, principal, logout, can, tenant } = useSession();
  if (loading) return <p className="center" role="status">Loading…</p>;
  if (!principal) return <LoginPage />;
  const needsTenant = principal.is_platform_admin && !tenant;

  return (
    <div className="layout">
      <header className="topbar">
        <span className="brand">
          <BrandMark />
          <span>
            <span className="brand-name">SignalFabric</span>
            <br />
            <span className="brand-sub">by Nasterra</span>
          </span>
        </span>
        <nav aria-label="Main">
          <NavLink to="/logs">Logs</NavLink>
          <NavLink to="/tail">Live tail</NavLink>
          <NavLink to="/issues">Issues</NavLink>
          {can("subscriptions:manage") && <NavLink to="/subscriptions">Subscriptions</NavLink>}
          {can("admin") && <NavLink to="/keys">API keys</NavLink>}
          {principal.is_platform_admin && <NavLink to="/tenants">Tenants</NavLink>}
        </nav>
        <div className="who">
          {principal.is_platform_admin && <TenantPicker />}
          <span className="whoami" title={principal.subject}>{principal.name || principal.subject}</span>
          <button type="button" className="ghost small" onClick={() => logout()}>
            Sign out
          </button>
        </div>
      </header>
      <main id="main">
        {needsTenant ? (
          <Routes>
            {/* Platform admin without a tenant selected: tenant management is still reachable
                (this is where the first tenant gets created); everything else needs a tenant. */}
            <Route path="/tenants" element={<TenantsPage />} />
            <Route
              path="*"
              element={
                <p className="notice">
                  Platform admin: choose a tenant from the top bar, or go to{" "}
                  <NavLink to="/tenants">Tenants</NavLink> to create one.
                </p>
              }
            />
          </Routes>
        ) : (
          <Routes>
            <Route path="/" element={<Navigate to="/logs" replace />} />
            <Route path="/logs" element={<ExplorerPage />} />
            <Route path="/tail" element={<TailPage />} />
            <Route path="/issues" element={<IssuesPage />} />
            <Route path="/issues/:id" element={<IssueDetailPage />} />
            <Route path="/subscriptions" element={<SubscriptionsPage />} />
            <Route path="/keys" element={<KeysPage />} />
            {principal.is_platform_admin && <Route path="/tenants" element={<TenantsPage />} />}
            <Route path="*" element={<p>Not found.</p>} />
          </Routes>
        )}
      </main>
    </div>
  );
}
